import { useEffect, useState } from "react";
import ConnectionListItem from "../ListItems/ConnectionListItem";
import ConnectionEditModal from "../Modals/ConnectionEditModal";
import BreadCrumb from "../Controls/BreadCrumb";
import UserData from "../Controls/UserData";
import Help from "../Controls/Help";
import CustomButton from "../Controls/CustomButton";
import { MdAddBox } from "react-icons/md";
import { useNavigate, useLocation } from "react-router-dom";
import API from "@src/API.js";
import { toast } from "react-toastify";

export default function Connections() {
  const navigate = useNavigate();
  const location = useLocation();
  const [userData, setUserData] = useState(() => UserData.UserData() || {});
  const [email, setEmail] = useState("");
  const [emailResult, setEmailResult] = useState(null);
  const [employmentId, setEmploymentId] = useState("");
  const [coworkers, setCoworkers] = useState([]);
  const [online, setOnline] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [connectionModalId, setConnectionModalId] = useState(() => {
    const fromState = location.state?.connectionModalId;
    return fromState != null ? Number(fromState) || 0 : null;
  });

  const employments = userData.employmentHistory || userData.employments || [];

  function refreshList() {
    setUserData(UserData.UserData() || {});
  }

  function closeConnectionModal() {
    setConnectionModalId(null);
    refreshList();
    if (location.state?.connectionModalId != null) {
      navigate("/connections", { replace: true, state: {} });
    }
  }

  async function refreshChat() {
    await API.postAsync("/Presence/Heartbeat", {});
    const o = await API.getAsync("/Chat/OnlineConnections");
    if (o?.ok) setOnline(o.jsonData || []);
    const s = await API.getAsync("/Chat/Sessions");
    if (s?.ok) setSessions(s.jsonData || []);
  }

  useEffect(() => {
    refreshChat();
    const t = setInterval(refreshChat, 30000);
    return () => clearInterval(t);
  }, []);

  async function findByEmail() {
    const trimmed = (email || "").trim();
    if (!trimmed.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    const resp = await API.getAsync("/Connection/FindByEmail?email=" + encodeURIComponent(trimmed));
    if (resp && resp.ok) {
      setEmailResult(resp.jsonData);
      if (!resp.jsonData?.found) toast.info("No active account with that email — use Invite to reach them");
    } else {
      toast.error(resp?.message || "Search failed");
    }
  }

  async function loadCoworkers() {
    const id = Number(employmentId);
    if (!id) {
      toast.error("Select an employment");
      return;
    }
    const resp = await API.getAsync("/Connection/CoworkersByEmployment?employmentId=" + id);
    if (resp && resp.ok && Array.isArray(resp.jsonData)) {
      setCoworkers(resp.jsonData);
      if (resp.jsonData.length === 0) toast.info("No overlapping searchable coworkers found");
    } else {
      toast.error(resp?.message || "Failed to load coworkers");
    }
  }

  async function requestChat(personId) {
    const resp = await API.postAsync("/Chat/Request", { ToPersonID: personId });
    if (resp?.ok) {
      toast.success("Chat request sent");
      const sid = resp.jsonData?.ID ?? resp.jsonData?.id;
      if (sid) navigate(`/chat/${sid}`);
      else await refreshChat();
    } else {
      toast.error(resp?.jsonData?.message || "Chat request failed");
    }
  }

  return (
    <div className="pagebody">
      <BreadCrumb props={["Connections"]} />
      <div className="tabbody">
        <h2>Connections</h2>
        <Help id="2141" />
        <span className="listTitleIcons">
          <MdAddBox onClick={() => navigate("/invite")} title="Invite someone" />
        </span>

        <div className="tabsection" data-testid="online-connections">
          <h3>Online now</h3>
          <p className="fieldHint">
            Coarse presence (last seen within ~5 minutes). Chat only with verified connections who are online.
          </p>
          {online.length === 0 ? (
            <p className="byline">No connections online right now.</p>
          ) : (
            <ul className="topList">
              {online.map((c) => {
                const pid = c.PersonID ?? c.personID;
                return (
                  <li key={pid}>
                    <div className="mobilePanel">
                      <span className="title">
                        {c.FirstName ?? c.firstName} {c.LastName ?? c.lastName}
                      </span>
                      <span className="byline">Online</span>
                      <CustomButton type="button" variant="contained" color="primary" onClick={() => requestChat(pid)}>
                        Request chat
                      </CustomButton>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {sessions.length > 0 ? (
          <div className="tabsection" data-testid="chat-sessions">
            <h3>Chat sessions</h3>
            <ul className="topList">
              {sessions.map((s) => {
                const sid = s.ID ?? s.id;
                return (
                  <li key={sid}>
                    <div className="mobilePanel">
                      <span className="title">
                        {(s.OtherFirstName ?? s.otherFirstName) + " " + (s.OtherLastName ?? s.otherLastName)}
                      </span>
                      <span className="byline">Status: {String(s.Status ?? s.status)}</span>
                      <CustomButton type="button" variant="contained" color="primary" onClick={() => navigate(`/chat/${sid}`)}>
                        Open
                      </CustomButton>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        <div className="tabsection">
          <h3>Find by email</h3>
          <p className="fieldHint">Exact email match only — no open people directory.</p>
          <div className="field">
            <input
              type="email"
              value={email}
              placeholder="colleague@example.com"
              onChange={(e) => setEmail(e.target.value)}
              aria-label="Find by email"
            />
            <CustomButton type="button" variant="contained" color="primary" onClick={findByEmail}>
              Find
            </CustomButton>
          </div>
          {emailResult?.found ? (
            <div className="mobilePanel">
              <span className="title">
                {emailResult.firstName} {emailResult.lastName}
              </span>
              <span className="byline">{emailResult.email}</span>
              <span className="rightText">
                {emailResult.alreadyConnected ? "Already connected" : "Not connected yet — use Invite with this email"}
              </span>
            </div>
          ) : null}
        </div>

        <div className="tabsection">
          <h3>Coworkers (overlapping tenure)</h3>
          <p className="fieldHint">People who marked employment searchable at the same employer while your dates overlap.</p>
          <div className="field">
            <select
              aria-label="Employment for coworker search"
              value={employmentId}
              onChange={(e) => setEmploymentId(e.target.value)}
            >
              <option value="">Select employment…</option>
              {employments.map((eh) => (
                <option key={eh.ID || eh.id} value={eh.ID || eh.id}>
                  {(eh.Title || eh.title || "Role") + " (" + (eh.ID || eh.id) + ")"}
                </option>
              ))}
            </select>
            <CustomButton type="button" variant="contained" color="primary" onClick={loadCoworkers}>
              Find coworkers
            </CustomButton>
          </div>
          <ul className="topList">
            {coworkers.map((c) => (
              <li key={(c.personId || c.PersonId) + "-" + (c.employmentId || c.EmploymentId)}>
                <div className="mobilePanel">
                  <span className="title">
                    {c.firstName || c.FirstName} {c.lastName || c.LastName}
                  </span>
                  <span className="byline">
                    {c.title || c.Title} · {c.employerName || c.EmployerName}
                  </span>
                  <span className="rightText">{c.connectionStatus || c.ConnectionStatus || "Not connected"}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <h3>Your connections</h3>
        <ul className="topList SkillList">
          {userData.connections && userData.connections.length > 0 ? (
            userData.connections.map((c) => (
              <li key={c.ID ?? c.id}>
                <ConnectionListItem
                  connection={c}
                  onEdit={(id) => setConnectionModalId(id)}
                />
              </li>
            ))
          ) : (
            <p>You have not added any connections</p>
          )}
        </ul>
      </div>
      {connectionModalId !== null ? (
        <ConnectionEditModal
          connectionId={connectionModalId}
          onClose={closeConnectionModal}
        />
      ) : null}
    </div>
  );
}
